import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import {
  CommentFilterOptionDefinition,
  CommentingResourceModel,
  CommentSortOptionDefinition,
  ThreadSort,
  ThreadViewModel,
} from '../../../models';
import {
  CloseAction,
  CloseThreadCreationAction,
  CreateThreadForResourceAction,
  DestroyAction,
  EditThreadAction,
  CloseEditAction,
  FocusResourceAction,
  OpenAction,
  ReplyToThreadAction,
  CloseReplyToThreadAction,
  EditCommentAction,
  InitializeCommentingPanelAction,
  SetResourceViewModelsAction,
  DisplaySingleThreadAction,
  CloseSingleThreadDisplayAction,
  FocusResourcesAction,
  FinishFocusingAction,
  ChangeSortingOptionAction,
  SetFilterOptionAction,
  AddNewResourcesAction,
  ClearStateAfterClosingAction,
  DisplayAllThreadsAction,
} from '../../actions';
import { createComposedResourceId } from '../../../utils';
import { User } from '@anecdotes/identity';
import { ThreadAction } from '@anecdotes/commenting';

export interface CommentingPanelState {
  isOpen: boolean;
  isInitialized: boolean;
  focusedResource: CommentingResourceModel;
  threadCreationFor: CommentingResourceModel;
  editingThreadId: string;
  editingCommentId: string;
  replyingThreadId: string;
  threadViewModels: ThreadViewModel[];
  areThreadsLoaded: boolean;
  resources: EntityState<CommentingResourceModel>;
  resourceType: string;
  users: User[];
  commonLogData: any;
  selectedSingleThreadResourceId: string;
  focusedResources: { [resourceName: string]: string };
  filterOptions: CommentFilterOptionDefinition[];
  selectedFilterOption: CommentFilterOptionDefinition;
  sortOptions: CommentSortOptionDefinition[];
  selectedSortOption: ThreadSort;
  hasUniqueSingleThreadDesign: boolean;
  shouldDisplayBackButton: boolean;
}

export function resourceIdSelector(resource: CommentingResourceModel): string {
  return createComposedResourceId(resource.resourceType, resource.resourceId);
}

const adapter = createEntityAdapter({
  selectId: resourceIdSelector,
});

const initialState: CommentingPanelState = {
  isOpen: false,
  isInitialized: false,
  focusedResource: null,
  threadCreationFor: null,
  resources: adapter.getInitialState(),
  resourceType: null,
  threadViewModels: null,
  areThreadsLoaded: false,
  users: null,
  editingThreadId: null,
  editingCommentId: null,
  replyingThreadId: null,
  commonLogData: null,
  selectedSingleThreadResourceId: null,
  focusedResources: null,
  filterOptions: [],
  selectedFilterOption: null,
  sortOptions: null,
  selectedSortOption: null,
  hasUniqueSingleThreadDesign: false,
  shouldDisplayBackButton: true,
};

const reducer = createReducer(
  initialState,
  on(OpenAction, (state) => {
    return {
      ...state,
      isOpen: true,
    };
  }),
  on(InitializeCommentingPanelAction, (state, { commentingConfig }) => {
    const resources = adapter.addMany(commentingConfig.resources, adapter.removeAll(state.resources));

    return {
      ...state,
      isInitialized: true,
      commonLogData: commentingConfig.commonLogData,
      users: commentingConfig.users,
      resources,
      filterOptions: commentingConfig.filterOptions,
      sortOptions: commentingConfig.sortOptions || null,
      resourceType: commentingConfig.resourceType,
      hasUniqueSingleThreadDesign: !!commentingConfig.hasUniqueSingleThreadDesign,
      shouldDisplayBackButton:
        commentingConfig.shouldDisplayBackButton === undefined ? true : commentingConfig.shouldDisplayBackButton,
    };
  }),
  on(AddNewResourcesAction, (state, { resources }) => {
    return {
      ...state,
      resources: adapter.addMany(resources, state.resources),
    };
  }),
  on(SetResourceViewModelsAction, (state, { threadViewModels }) => {
    return {
      ...state,
      threadViewModels,
      areThreadsLoaded: true,
    };
  }),
  on(SetFilterOptionAction, (state, { selectedFilterOption }) => {
    return { ...state, selectedFilterOption };
  }),
  on(ChangeSortingOptionAction, (state, action) => {
    return {
      ...state,
      selectedSortOption: action.selectedSortOption,
    };
  }),
  on(CloseAction, (state) => {
    return {
      ...state,
      isOpen: false,
    };
  }),
  on(ClearStateAfterClosingAction, (state) => {
    return {
      ...state,
      focusedResource: null,
      focusedResources: null,
      selectedSortOption: null,
      selectedFilterOption: null,
      selectedSingleThreadResourceId: null,
      threadCreationFor: null,
    };
  }),
  on(DestroyAction, () => {
    return { ...initialState };
  }),
  on(FocusResourceAction, (state, action) => {
    return {
      ...state,
      isOpen: true,
      threadCreationFor: null,
      focusedResource: state.resources.entities[createComposedResourceId(action.resourceType, action.resourceId)],
    };
  }),
  on(CreateThreadForResourceAction, (state, action) => {
    return {
      ...state,
      focusedResource: null,
      isOpen: true,
      editingThreadId: null,
      threadCreationFor: state.resources.entities[createComposedResourceId(action.resourceType, action.resourceId)],
    };
  }),
  on(CloseThreadCreationAction, (state) => {
    return { ...state, focusedResource: null, threadCreationFor: null };
  }),
  on(EditThreadAction, (state, action) => {
    return { ...state, threadCreationFor: null, editingThreadId: action.threadId };
  }),
  on(EditCommentAction, (state, action) => {
    return { ...state, threadCreationFor: null, editingCommentId: action.commentId };
  }),
  on(CloseEditAction, (state) => {
    return { ...state, threadCreationFor: null, editingCommentId: null, editingThreadId: null };
  }),
  on(ReplyToThreadAction, (state, action) => {
    return { ...state, replyingThreadId: action.threadId };
  }),
  on(CloseReplyToThreadAction, (state) => {
    return { ...state, replyingThreadId: null };
  }),
  on(DisplayAllThreadsAction, (state) => {
    return {
      ...state,
      isOpen: true,
      selectedSingleThreadResourceId: null,
      focusedResource: null,
      focusedResources: null,
    };
  }),
  on(DisplaySingleThreadAction, (state, action) => {
    return {
      ...state,
      selectedSingleThreadResourceId: action.resourceId,
      isOpen: true,
    };
  }),
  on(CloseSingleThreadDisplayAction, (state) => {
    return {
      ...state,
      selectedSingleThreadResourceId: null,
      focusedResource: null,
      focusedResources: null,
      threadCreationFor: null,
    };
  }),
  on(FocusResourcesAction, (state, action) => {
    return {
      ...state,
      focusedResources: { ...state.focusedResources, ...action.focusingResourcesMap },
    };
  }),
  on(FinishFocusingAction, (state, action) => {
    const copy = { ...state.focusedResources };
    delete copy[action.resourceName];

    return {
      ...state,
      focusedResources: copy,
    };
  }),
  on(ThreadAction.Created, (state, action) => {
    if (
      state.threadCreationFor &&
      state.threadCreationFor.resourceId === action.thread.resourceId &&
      state.threadCreationFor.resourceType === action.thread.resourceType
    ) {
      return {
        ...state,
        threadCreationFor: null,
      };
    }
    return state;
  }),
);

export function commentingPanelReducer(state = initialState, action: Action): CommentingPanelState {
  return reducer(state, action);
}
